import React, { useEffect, useMemo, useState } from 'react';
import { H3 } from 'tea-component';
import CommunityChart from './chart';
import BubbleCommon from '@/pages/opinion/components/BubbleCommon';
import { engagementText } from '@/pages/opinion/components/BubbleCommon/copywritingKey/community';
import CardCommon from '@/pages/opinion/components/CardCommon';
import { useIntl } from 'umi';
import {
  arrayToSimpleObj,
  getRationPercentValue,
  getShowCount,
  pressTimeGrouping,
  supplementSingleData,
} from '@/pages/opinion/utils';
import { getCommunityVideoTimeLine } from '@/pages/opinion/services/video';
import RingRatioBubble from '@/pages/opinion/components/Snippets/RingRatioBubble';
import {
  reviewsFormat,
} from '@/pages/opinion/utils/value';
import PercentageItem from '@/pages/opinion/components/Snippets/PercentageItem';
import { barIcon, lineIcon } from '@/pages/opinion/constants/chart';
import { formatDateFunc } from '@op/utils/time';
import './index.scss';
import VideoDownload from '../VideoDownload';
import { isSpecialEdition } from '../utils';
import { DBO_V } from '@op/utils/format';
import EngagementTypeSegment from './EngagementTypeSegment';
import useLanguage from '@op/hooks/useLanguage';

export default function CommunityCard({
  type = 'counts',
  keepParams = {},
  gameInfo = {},
}: any) {
  const { formatMessage } = useLanguage();
  const [visible, setVisible] = useState(false);
  const { title, pureTitle } = useMemo(() => {
    const cardMap: any = {
      counts: {
        title: formatMessage({ id: 'Video Counts' }),
      },
      engagements: {
        pureTitle: 'Video Engagement',
        title: (
          <div style={{ display: 'flex' }}>
            {formatMessage({ id: 'Video Engagement' })}
            <BubbleCommon
              tipsMap={engagementText}
              className="title-icon"
              placement="top"
              iconStyle={{ marginLeft: 6 }}
            ></BubbleCommon>
          </div>
        ),
      },
      views: {
        title: formatMessage({ id: 'Video Views' }),
      },
    };
    return cardMap[type];
  }, [type, formatMessage]);

  const [data, setData] = useState<any>({});
  const [loading, setLoading] = useState(true);

  const [engagementType, setEngagementType] = useState('engagements');
  const isEngagementRate = engagementType === 'engagement_per_view';

  const formatMetricValue = useMemo(() => {
    return isEngagementRate ? DBO_V.ratio_min : getShowCount;
  }, [isEngagementRate]);

  const metrics = useMemo(() => {
    return [type === 'engagements' ? engagementType : type];
  }, [engagementType, type]);

  useEffect(() => {
    if (!keepParams?.edition_unified_id) return;
    setData({});
    setLoading(true);

    const params = {
      ...keepParams,
      metric: type === 'engagements' ? engagementType : type,
    };

    getCommunityVideoTimeLine(params).then((data: any) => {
      if (data) {
        const {
          list,
          timeline_of_end_time,
          timeline_of_start_time,
          link_of_end_time,
          link_of_start_time,
          total_new,
          total_old,
        } = data || {};
        const format =
          params.date_type === 'hourly' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
        const newList = supplementSingleData(
          timeline_of_start_time,
          timeline_of_end_time,
          list?.map((item) => ({ ...item, name: item.key })),
          params.date_type,
        );
        const timeGroup = pressTimeGrouping(newList, format);
        const result = Object.entries(timeGroup).map(([key, elements]) => {
          const y_axis: Record<string, any> = {};

          elements.forEach(({ name, time, value }) => {
            y_axis[name] = value;
            key = time; // or use a separate variable if you don't want to reassign
          });

          return {
            x_axis: key,
            y_axis,
          };
        });
        setData({
          statistics: result,
          count: total_new,
          ratio: total_old,
          ratio_val: getRationPercentValue(total_new, total_old, 2),
          ratio_time_line: [link_of_start_time, link_of_end_time],
          time_line: [params.start_time, params.end_time],
        });
      } else {
        setData({});
      }
      setLoading(false);
    });
  }, [keepParams, engagementType, type]);

  const engagementTypeOptions = useMemo(() => {
    return [
      {
        text: formatMessage({ id: 'Engagement' }),
        value: 'engagements',
      },
      ...(isSpecialEdition(gameInfo) ? [
        {
          text: formatMessage({ id: 'Engagement Rate' }),
          value: 'engagement_per_view',
        },
      ] : []),
      {
        text: formatMessage({ id: 'Likes' }),
        value: 'likes',
      },
      {
        text: formatMessage({ id: 'Comments' }),
        value: 'comments',
      },
      {
        text: formatMessage({ id: 'Shares' }),
        value: 'shares',
      },
    ];
  }, [formatMessage, gameInfo]);

  const engagementTypeOptionsMap = arrayToSimpleObj(engagementTypeOptions);

  const [engagementTypeText, setEngagementTypeText] = useState('Engagement');

  const [chartType, setChartType] = useState('bar');

  const ringRatioBubbleTitle = useMemo(() => {
    if (pureTitle) {
      return (`${engagementTypeOptionsMap[engagementType]} ${formatMessage({ id: 'Change%' })}`);
    }

    return `${title} ${formatMessage({ id: 'Change%' })}`;
  }, [engagementType, title]);

  const formatDate = formatDateFunc(keepParams?.date_type);

  const { statistics, count, ratio, ratio_val, ratio_time_line, time_line } =
    useMemo(() => {
      return data || {};
    }, [data]);

  const handleEngagementTypeChange = (value: string): void => {
    setEngagementType(value);
    const matchedOption = engagementTypeOptions.find(
      (item) => item.value === value,
    );
    setEngagementTypeText(matchedOption?.text ?? 'Engagement');
  };

  return (
    <>
      <CardCommon
        title={<H3 style={{ marginRight: 15 }}>{title}</H3>}
        className={`video-card-item ${type === 'engagement' ? 'engagement-title' : ''}`}
        hasDownload
        operations={[barIcon, lineIcon]}
        handleOperationsOnClick={({ symbol }) => setChartType(symbol)}
        handleDownloadOnClick={() => setVisible(true)}
        subFilterBeforeElement={(
          <>
            {type === 'engagements' && (
              <EngagementTypeSegment
                value={engagementType}
                options={engagementTypeOptions}
                onChange={handleEngagementTypeChange}
              />
            )}
          </>
        )}
      >
        <>
          <div className="video-flex">
            <span className="video-flex-num">
              {typeof count !== 'undefined' ? formatMetricValue(count) : '-'}
            </span>
            <RingRatioBubble
              newTime={time_line}
              oldTime={ratio_time_line}
              oldValue={ratio}
              newValue={count}
              formateValue={isEngagementRate ? DBO_V.ratio_min : reviewsFormat}
              formateDate={(value) => formatDate(value)}
              title={ringRatioBubbleTitle}
            >
              <PercentageItem percentage={ratio_val}></PercentageItem>
            </RingRatioBubble>
          </div>
          <CommunityChart
            chartType={chartType}
            statistics={statistics}
            type={type}
            loading={loading}
            keepParams={keepParams}
            engagementTypeText={engagementTypeText}
            engagementType={engagementType}
          ></CommunityChart>
          <VideoDownload
            visible={visible}
            filter={keepParams}
            setVisible={setVisible}
            gameInfo={gameInfo}
            type={type}
            defaultSelect={metrics}
            showEngagementRate={isSpecialEdition(gameInfo)}
          ></VideoDownload>
        </>
      </CardCommon>
    </>
  );
}
